<script setup lang="ts">
/**
 * Désactiver ou réactiver une surveillance (gestes 11 et 12, route 5 ;
 * `dialogueHorsService` S:5320-5343 et `dialogueRemettre` S:5390-5396).
 *
 * Ce que l'utilisateur doit vivre : il désactive en un clic, en choisissant
 * un motif s'il le veut (un clic sur un motif désactive), ou sans préciser ;
 * une précision libre est facultative. Le motif n'est jamais exigé, et une
 * désactivation sans motif ne se dit jamais « autre » : le corps ne porte
 * alors aucun motif. Réactiver demande une confirmation.
 *
 * Le dialogue n'écrit rien lui-même : il rend le corps de la route 5 par
 * `valider` ; l'écran l'envoie, et le dialogue reste ouvert, bloqué, jusqu'à
 * la réponse.
 */
// ================================
// IMPORTS
// ================================
import { ref } from "vue"
import { useI18n } from "vue-i18n"
import type {
    AlarmesV2Modification,
    AlarmesV2MotifDesactivation,
} from "@modules/alarmes-v2/types/alarmes-v2"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        mode: "desactiver" | "reactiver"
        /** Vrai pendant l'envoi et la relecture : rien ne repart. */
        enCours?: boolean
        /** La phrase d'un refus du serveur, `""` sinon. */
        refus?: string
    }>(),
    { enCours: false, refus: "" }
)

const emit = defineEmits<{
    valider: [corps: AlarmesV2Modification]
    fermer: []
}>()

/** Les cinq motifs, dans l'ordre de la maquette (S:2392-2398). */
const MOTIFS: AlarmesV2MotifDesactivation[] = [
    "ne_sert_plus",
    "equipement_retire",
    "doublon",
    "erreur_de_declaration",
    "autre",
]

const precisionOuverte = ref(false)
const precision = ref("")

/** Un libellé avec sa majuscule (S:5327). */
function libelleDuMotif(m: AlarmesV2MotifDesactivation): string {
    const l = t(`alarmesV2.motifsDesactivation.${m}`)
    return l.charAt(0).toUpperCase() + l.slice(1)
}

// ================================
// HANDLERS
// ================================
/**
 * Désactive : le motif s'il est choisi, la précision si elle est écrite,
 * rien d'autre.
 */
function onDesactiver(motif: AlarmesV2MotifDesactivation | null): void {
    if (props.enCours) return
    const corps: AlarmesV2Modification = { etat: "desactivee" }
    if (motif) corps.desactivation_motif = motif
    const texte = precision.value.trim()
    if (texte) corps.desactivation_texte = texte.slice(0, 1000)
    emit("valider", corps)
}

function onReactiver(): void {
    if (props.enCours) return
    emit("valider", { etat: "armee" })
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <CadreDialogue
        :titre="
            mode === 'desactiver'
                ? t('alarmesV2.actions.desactiverDialogue.titre')
                : t('alarmesV2.actions.reactiverDialogue.titre')
        "
        :bloque="enCours"
        @fermer="emit('fermer')"
    >
        <template #intro>
            <p class="text-sm text-surface-600">
                {{
                    mode === "desactiver"
                        ? t("alarmesV2.actions.desactiverDialogue.texte")
                        : t("alarmesV2.actions.reactiverDialogue.texte")
                }}
            </p>
        </template>

        <template v-if="mode === 'desactiver'">
            <p class="text-sm font-semibold">
                {{ t("alarmesV2.actions.desactiverDialogue.pourquoi") }}
            </p>
            <div class="flex flex-wrap gap-2">
                <button
                    v-for="m in MOTIFS"
                    :key="m"
                    type="button"
                    class="rounded border border-surface-300 px-3 py-1 text-sm"
                    :aria-disabled="enCours ? 'true' : undefined"
                    :data-test="`motif-${m}`"
                    @click="onDesactiver(m)"
                >
                    {{ libelleDuMotif(m) }}
                </button>
            </div>
            <button
                v-if="!precisionOuverte"
                type="button"
                class="self-start text-sm text-primary-700 underline"
                data-test="ajouter-precision"
                @click="precisionOuverte = true"
            >
                {{ t("alarmesV2.actions.desactiverDialogue.ajouterPrecision") }}
            </button>
            <label v-else class="flex flex-col gap-1 text-sm">
                {{ t("alarmesV2.actions.desactiverDialogue.precision") }}
                <textarea
                    v-model="precision"
                    maxlength="1000"
                    rows="2"
                    class="rounded border border-surface-300 p-2"
                    :placeholder="
                        t('alarmesV2.actions.desactiverDialogue.exemple')
                    "
                    data-test="precision"
                />
            </label>
        </template>

        <p v-if="enCours" class="text-sm text-surface-700" role="status">
            {{ t("alarmesV2.actions.envoi") }}
        </p>
        <p
            v-else-if="refus"
            class="text-sm text-red-800"
            role="alert"
            data-test="refus"
        >
            {{ refus }}
        </p>

        <template #pied>
            <button
                type="button"
                class="rounded border border-surface-300 px-3 py-1.5 text-sm"
                :aria-disabled="enCours ? 'true' : undefined"
                data-focus-initial
                data-test="annuler"
                @click="!enCours && emit('fermer')"
            >
                {{ t("alarmesV2.actions.annuler") }}
            </button>
            <button
                v-if="mode === 'desactiver'"
                type="button"
                class="rounded border border-red-700 bg-red-700 px-3 py-1.5 text-sm font-medium text-white"
                :aria-disabled="enCours ? 'true' : undefined"
                data-test="sans-preciser"
                @click="onDesactiver(null)"
            >
                {{ t("alarmesV2.actions.desactiverDialogue.sansPreciser") }}
            </button>
            <button
                v-else
                type="button"
                class="rounded border border-primary-600 bg-primary-600 px-3 py-1.5 text-sm font-medium text-white"
                :aria-disabled="enCours ? 'true' : undefined"
                data-test="confirmer"
                @click="onReactiver"
            >
                {{ t("alarmesV2.actions.reactiver") }}
            </button>
        </template>
    </CadreDialogue>
</template>
